import {
    ChangeDetectionStrategy,
    Component, ViewChild, AfterViewInit, ElementRef, Output, Input, EventEmitter, OnChanges, SimpleChanges, OnDestroy, NgZone,
    ChangeDetectorRef, inject
} from "@angular/core";

import Cropper, {
    CropperImage, CropperSelection
} from "cropperjs";
import { MimeType } from "@shopliftr/common-js/data";
import {
    DomSanitizer,
    SafeUrl
} from "@angular/platform-browser";
import { ICroppedImage } from "./interface/cropped-image";
import {
    MatIconButton
} from "@angular/material/button";
import { MatIcon } from "@angular/material/icon";
import { FormsModule } from "@angular/forms";
import {
    MatFormField, MatLabel, MatError, MatSuffix
} from "@angular/material/form-field";
import { MatInput } from "@angular/material/input";
import { MatTooltip } from "@angular/material/tooltip";
import { EnhancedControlDirective } from "../../../forms/directives/form-control/enhanced-control.directive";
import { ClearButtonComponent } from "../clear-button/clear-button.component";
import { ErrorMessageComponent } from "../../error-message/error-message.component";
import { ButtonDirective } from "../../../application/directives/button/button.directive";

@Component({
    selector: "mgd-image-cropper",
    templateUrl: "./image-cropper.component.html",
    styleUrls: ["./image-cropper.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatIcon,
        FormsModule,
        MatFormField,
        MatLabel,
        MatError,
        MatSuffix,
        MatInput,
        MatTooltip,
        MatIconButton,
        EnhancedControlDirective,
        ClearButtonComponent,
        ErrorMessageComponent,
        ButtonDirective
    ]
})

/**
 * Allows the user to crop, resize and rotate an image.
 *
 * The image url is provided using the imageSource input. This will often be a url to a blob coming from the FileSelectorComponent.
 *
 * The optional inputs internalCropHeight, internalCropWidth are responsible for
 * setting the max width and max height for the cropped image.
 *
 * The lockDimensions and hideSetDimensions inputs are responsible for locking the output dimensions.
 * If the hideSetDimensions input is not set user can input the dimensions for the cropped image.
 *
 * It has one output, the cropped Image resulting from the cropped part of the image
 *
 * The component has a default width and height of 400px, but it can be resized using css.
 *
 * Example:
 *
 * mgd-image-cropper {
 *    width: 500px;
 *    height: 500px;
 * }
 */
export class ImageCropperComponent implements AfterViewInit, OnChanges, OnDestroy {

    /**
     * Sub-pixel tolerance (in canvas-relative CSS px) used when comparing the current
     * selection's bounds against the image's own rendered bounds to decide whether the
     * image has actually been cropped. Guards against `getBoundingClientRect()`
     * sub-pixel/layout-rounding differences between the two measurement paths (a live
     * DOM rect vs. the selection element's own internally-tracked x/y/width/height)
     * being mistaken for a genuine (if tiny) crop.
     */
    private static readonly _EDGE_TOLERANCE_PX = 1;

    @ViewChild("image", { static: false }) imageElement!: ElementRef<HTMLImageElement>;

    /**
     * cropper output event within the interface
     */
    @Output() crop: EventEmitter<ICroppedImage> = new EventEmitter<ICroppedImage>(false);

    @Input() internalCropHeight: number;

    @Input() internalCropWidth: number;

    @Input() lockDimensions: boolean;

    @Input() hideSetDimensions: boolean;

    @Input() imageSource: string;

    /**
     * MIME type used to encode the cropped output blob. Defaults to MimeType.Png.
     * Note: This was changed from Jpeg to png because PNG preserves the alpha channel
     * and maintains transparency.
     *
     * canvas.toBlob reliably supports image/png, image/jpeg, and image/webp;
     * an unsupported MIME type silently falls back to image/png in the browser.
     */
    @Input() mimeType: MimeType = MimeType.Png;

    isOpen?: boolean;

    safeImageUrl: SafeUrl;

    private _cropper?: Cropper;

    private _cropperImage?: CropperImage | null;

    private _cropperSelection?: CropperSelection | null;

    private _viewInitialized = false;

    /**
     * Monotonically increasing token identifying the most recent `_onCrop()` invocation.
     * `CropperSelection` can raise several "change" events in rapid succession against the
     * SAME shared instance (cropperjs's own default-coverage init selection settling, this
     * component's own `_selectFullImage()`, and/or further user-driven changes) — each spawns
     * its own concurrent, independently-async `_onCrop()` call. Since every one of those calls
     * reads/writes the one shared `_cropperSelection` instance, a still in-flight EARLIER call
     * can resume (e.g. after its own `$nextTick()`/`$toCanvas()` await) only AFTER a LATER
     * call has already overwritten that instance's x/y/width/height — corrupting the earlier
     * call's own (by-then-stale) computation with a mismatched selection. See the guards in
     * `_onCrop()` that compare against this token to abandon any invocation a newer one has
     * superseded, so only the LAST ("winning") change of a burst ever actually exports/emits.
     */
    private _cropSequence = 0;

    private readonly _sanitizer = inject(DomSanitizer);
    private readonly _ngZone = inject(NgZone);
    private readonly _cdRef = inject(ChangeDetectorRef);

    ngAfterViewInit(): void {
        this._viewInitialized = true;
        this._loadCropper();
    }

    ngOnChanges(changes: SimpleChanges): void {
        /**
         * Change the image source for the instance image cropper
         */
        if (changes.imageSource) {
            this._destroyCropper();
            if (this.imageSource) {
                this.safeImageUrl = this._sanitizer.bypassSecurityTrustUrl(this.imageSource);
            }
            else {
                this.safeImageUrl = undefined;
            }
            // ngOnChanges runs before Angular flushes the new `<img src>` to the DOM.
            // Defer to a microtask so the cropper initializes against the new image.
            // Angular always fires ngOnChanges once on creation, before ngAfterViewInit runs.
            // Without this guard, the deferred reload below would race with
            // ngAfterViewInit's synchronous _loadCropper() call and construct two
            // Cropper instances on the initial load; skip it until the view exists,
            // since ngAfterViewInit already handles the very first load.
            if (this._viewInitialized) {
                void Promise.resolve().then(() => this._loadCropper());
            }
        }
    }

    ngOnDestroy(): void {

        this._destroyCropper();
    }

    /**
     * Event handler for the rotate button
     * Rotates the image 90 degrees to the right
     *
     * `$rotate()` transforms the IMAGE, not the selection's own x/y/width/height, and a
     * non-`dynamic` `CropperSelection` does not react to image transforms on its own — so no
     * "change" event fires and `_onCrop()` would otherwise never re-run. Explicitly re-trigger
     * the crop export so the rotated image is reflected in the next `crop` output, since
     * consumers store only the last-emitted `crop` value and do not re-request one at submit
     * time.
     */
    onRotateClick(event?: Event): void {
        event?.preventDefault();
        this._cropperImage?.$rotate("90deg");
        void this._onCrop();
    }

    /**
     * Event handler for the zoom in button
     * zooms in the crop box canvas causing the image displayed in the crop box to grow
     *
     * See `onRotateClick`'s doc comment: `$zoom()` transforms the image, not the selection, so
     * no "change" event fires on its own and `_onCrop()` must be re-triggered explicitly.
     */
    onZoomInClick(event?: Event): void {
        event?.preventDefault();
        this._cropperImage?.$zoom(0.1);
        void this._onCrop();
    }

    /**
     * Event handler for the zoom out button
     * causing the image displayed in the crop box to shrink
     *
     * See `onRotateClick`'s doc comment: `$zoom()` transforms the image, not the selection, so
     * no "change" event fires on its own and `_onCrop()` must be re-triggered explicitly.
     */
    onZoomOutClick(event?: Event): void {
        event?.preventDefault();
        this._cropperImage?.$zoom(-0.1);
        void this._onCrop();
    }

    /**
     * Locks cropped output image max width and height to the user input
     */
    onLockClick(): void {

        if (this._cropperSelection) {
            this.lockDimensions = !this.lockDimensions;
            if (this.lockDimensions && this.internalCropHeight && this.internalCropWidth) {
                this._setAspectRatio();
            }
            else {
                this._cropperSelection.aspectRatio = NaN;
            }
        }
    }

    private _setAspectRatio(): void {
        if (this._cropperSelection) {
            this._cropperSelection.aspectRatio = this.internalCropWidth / this.internalCropHeight;
        }
    }

    /**
     * @param changeDetail The triggering `CropperSelection` "change" event's `detail` payload
     * (its NEW x/y/width/height), when this call originates from that event. cropperjs emits the
     * "change" event synchronously from inside `$change()` BEFORE it assigns the new x/y/width/
     * height onto the `CropperSelection` instance itself (confirmed by tracing `cropper.esm.js`:
     * `$change()` calls `this.$emit(EVENT_CHANGE, { x, y, width, height })` — which runs this
     * method's listener synchronously — several lines before `this.x = x; ...; this.height =
     * height;`). So reading `this._cropperSelection.width/height` directly inside a "change"
     * handler (as this method used to) always returns the PREVIOUS selection's size, not the one
     * that just triggered the event — every "change" during this component's lifetime observed
     * the exact same stale value in practice. Prefer the event's own `detail`, which already
     * carries the authoritative new values; only fall back to the (possibly stale) live property
     * for the rare defensive case where this is invoked without a triggering event.
     */
    private async _onCrop(changeDetail?: {
        x: number;
        y: number;
        width: number;
        height: number;
    }): Promise<void> {

        if (!this._cropperSelection || !this.imageElement || !this._cropperImage) {
            return;
        }
        // Claim this invocation as the current "latest" one. `CropperSelection` can raise
        // several "change" events in rapid succession against the SAME shared instance
        // (cropperjs's own default-coverage init selection settling, `_selectFullImage()`,
        // and/or further changes) — each spawns its own concurrent, independently-async
        // `_onCrop()` call, all reading/writing that one shared instance. If a newer call
        // claims this token before this one resumes from an `await`, this instance's
        // x/y/width/height (and `$toCanvas()`'s own internal use of them, below) may already
        // belong to that NEWER change — so this call must abandon its work rather than risk
        // exporting/emitting a crop built from a mismatched mix of old and new selection state.
        const sequence = ++this._cropSequence;
        const selectionWidth = changeDetail?.width ?? this._cropperSelection.width;
        const selectionHeight = changeDetail?.height ?? this._cropperSelection.height;
        // `CropperSelection.$toCanvas()` (called below) does its OWN aspect-ratio-preserving
        // size adjustment internally, using `this.width`/`this.height` at the moment it's
        // called — which, for the same reason `changeDetail` is preferred above, are still the
        // PREVIOUS selection's stale values here (this method's synchronous execution up to its
        // first `await` runs nested inside the very same `$change()` call that triggered it, and
        // `$change()` doesn't assign its new x/y/width/height onto the instance until after this
        // event's listeners return). Deferring one microtask via `$nextTick()` — cropperjs's own
        // documented way to wait for its next DOM update cycle — lets `$change()` finish that
        // assignment first, so `$toCanvas()`'s internal aspect-ratio math is computed against the
        // CURRENT selection instead of the one it just superseded.
        await this._cropperSelection.$nextTick();
        if (sequence !== this._cropSequence || !this._cropperSelection || !this.imageElement || !this._cropperImage) {
            return;
        }
        let croppedCanvasOptions: {
            width: number;
            height: number;
        };
        if (this.lockDimensions) {
            croppedCanvasOptions = {
                width: this.internalCropWidth,
                height: this.internalCropHeight
            };
        }
        else {
            // Without an explicit size, `$toCanvas()` exports at the selection's
            // on-screen CSS-pixel size, not the source image's natural resolution: e.g.
            // an 864x409 image displayed scaled down to fit a 300x200 host renders a
            // full-image selection at ~150x71 rendered px, silently downsampling the
            // export instead of preserving the real resolution.
            //
            // `$getTransform()`'s basis-vector magnitudes are NOT reliably up to date at
            // this point: read immediately after `$ready()` fires, `$getTransform()` can
            // still report the identity matrix even though the image's "contain"-fit CSS
            // transform has already visually applied (confirmed via an isolated repro
            // against real cropperjs — `getBoundingClientRect()` on `<cropper-image>` at
            // that same moment already reflects the scaled-down rendered size, while
            // `$getTransform()` lags behind). cropperjs updates the rendered transform
            // before its own internally-tracked matrix state catches up, so deriving the
            // scale from `$getTransform()` here produced scaleX=scaleY=1 and silently
            // exported at on-screen size instead of natural resolution.
            //
            // Derive the scale from the same `getBoundingClientRect()`-based measurement
            // `_getImageBoundsRelativeToCanvas()` already uses reliably elsewhere in this
            // file (`_selectFullImage()`, and the `imageCropped` comparison below) instead:
            // the ratio of the image's current rendered width/height to its natural
            // width/height. Dividing the selection's rendered size by that scale converts
            // it back to a natural-resolution-equivalent size, so a full-image selection
            // exports at (approximately) the image's real natural resolution instead of
            // its current on-screen size.
            const imageBounds = this._getImageBoundsRelativeToCanvas();
            const naturalWidth = this.imageElement.nativeElement.naturalWidth;
            const naturalHeight = this.imageElement.nativeElement.naturalHeight;
            const scaleX = (imageBounds && naturalWidth) ? imageBounds.width / naturalWidth : 1;
            const scaleY = (imageBounds && naturalHeight) ? imageBounds.height / naturalHeight : 1;
            croppedCanvasOptions = {
                width: Math.round(selectionWidth / scaleX),
                height: Math.round(selectionHeight / scaleY)
            };
        }
        const canvas = await this._cropperSelection.$toCanvas(croppedCanvasOptions);
        if (sequence !== this._cropSequence) {
            // A newer change superseded this one while `$toCanvas()` was rendering; its own
            // cycle will export/emit the correct, up-to-date crop instead.
            return;
        }
        canvas?.toBlob((blob: Blob | null) => {

            if (blob && this._cropperSelection && sequence === this._cropSequence) {
                const naturalWidth = this.imageElement.nativeElement.naturalWidth;
                const naturalHeight = this.imageElement.nativeElement.naturalHeight;
                const {
                    x, y, width, height
                } = this._cropperSelection;
                // Comparing the selection's rendered/canvas-relative CSS-px x/y/width/height
                // directly against naturalWidth/naturalHeight (bitmap px) was only correct by
                // coincidence when the canvas happened to render at the image's natural
                // resolution. Compare against the image's own rendered bounds instead — both
                // sides are then in the same canvas-relative CSS-px coordinate space, so no
                // unit conversion is needed at all. This mirrors `_selectFullImage`'s use of
                // the same two `getBoundingClientRect()` calls to define "the whole image."
                const imageBounds = this._getImageBoundsRelativeToCanvas();
                const imageCropped = !imageBounds
                    || Math.round(x) > Math.round(imageBounds.x) + ImageCropperComponent._EDGE_TOLERANCE_PX
                    || Math.round(y) > Math.round(imageBounds.y) + ImageCropperComponent._EDGE_TOLERANCE_PX
                    || Math.round(x + width) < Math.round(imageBounds.x + imageBounds.width) - ImageCropperComponent._EDGE_TOLERANCE_PX
                    || Math.round(y + height) < Math.round(imageBounds.y + imageBounds.height) - ImageCropperComponent._EDGE_TOLERANCE_PX;

                let imageSizeRelativeToTarget: ICroppedImage["imageSizeRelativeToTarget"] = "unknown";
                if (this.lockDimensions && this.internalCropWidth && this.internalCropHeight) {
                    if (naturalWidth < this.internalCropWidth || naturalHeight < this.internalCropHeight) {
                        imageSizeRelativeToTarget = "smaller";
                    }
                    else if (naturalWidth === this.internalCropWidth && naturalHeight === this.internalCropHeight) {
                        imageSizeRelativeToTarget = "exact";
                    }
                    else {
                        imageSizeRelativeToTarget = "larger";
                    }
                }

                // Update tracked dimensions after the comparison so target values
                // are not overwritten before the check runs.
                this.internalCropWidth = canvas.width;
                this.internalCropHeight = canvas.height;

                // canvas.toBlob() runs outside Angular's zone; re-enter so that
                // parent bindings that react to the `crop` output update via change detection.
                this._ngZone.run(() => {
                    this.crop.emit({
                        width: canvas.width,
                        height: canvas.height,
                        image: blob,
                        mimeType: this.mimeType,
                        imageCropped,
                        imageSizeRelativeToTarget
                    });
                    this._cdRef.markForCheck();
                });
            }
        }, this.mimeType, 1);
    }

    private _destroyCropper(): void {

        this._cropper?.destroy();
        this._cropper = undefined;
        this._cropperImage = undefined;
        this._cropperSelection = undefined;
    }

    /**
     * Load cropper js instance with a image element.
     *
     * Guarded on `imageSource` as well as the view child: without an image to
     * crop, constructing a real Cropper wraps the `<img>` at its unset/page
     * URL, which the browser cannot decode as an image and which surfaces as
     * an unhandled promise rejection from the cropper-image element's
     * internal readiness check.
     */
    private _loadCropper(): void {

        if (this.imageElement && this.imageSource) {
            this._cropper = new Cropper(this.imageElement.nativeElement);
            this._cropperImage = this._cropper.getCropperImage();
            this._cropperSelection = this._cropper.getCropperSelection();
            this._cropperSelection?.addEventListener("change", (event: Event) => {
                const changeDetail = (event as CustomEvent<{
                    x: number;
                    y: number;
                    width: number;
                    height: number;
                }>).detail;
                void this._onCrop(changeDetail);
            });
            if (this.lockDimensions && this.internalCropWidth && this.internalCropHeight) {
                this._setAspectRatio();
            }
            // cropperjs's own `initial-coverage` template attribute (used by
            // CropperSelection's `$initSelection()`/`$reset()`) is a percentage of the
            // <cropper-canvas>, not of the <cropper-image>'s own visible bounds. Whenever
            // the loaded image's natural aspect ratio differs from the canvas's aspect
            // ratio (the common case — the canvas is sized by this component's CSS, not by
            // the image), the canvas letterboxes the image and any canvas-relative coverage
            // value necessarily includes some of that empty letterbox padding, so the
            // default selection never matches "the whole image, nothing else." Once the
            // image is actually ready (and therefore laid out), explicitly select its real
            // rendered bounds instead.
            void this._cropperImage?.$ready(() => {
                this._selectFullImage();
            });
        }
    }

    /**
     * Sets the crop selection to exactly match `<cropper-image>`'s own rendered bounds
     * (relative to `<cropper-canvas>`), rather than a canvas-relative coverage percentage.
     * Used for the initial selection once the image is ready — cropperjs's own `$reset()`
     * would instead restore whatever canvas-relative `$initSelection()` computed at connect
     * time, which does not account for letterboxing when the image and canvas aspect ratios
     * differ.
     *
     * When the selection has a locked `aspectRatio` (set via `_setAspectRatio`), the largest
     * same-ratio rectangle that fits *within* (not merely overlaps) the image's rendered
     * bounds is computed here and passed to `$change` together with an explicit `NaN`
     * aspect ratio argument. `CropperSelection.$change()`'s own internal size adjustment
     * (triggered whenever it is given a positive `aspectRatio`) always runs in `'cover'`
     * mode — it grows one dimension until the result is at least as large as both inputs —
     * so asking it to fit the image's raw width/height at a locked ratio can expand the
     * selection well past the image's actual edges. Passing `NaN` skips that internal
     * adjustment since the "contain" math has already been done here; the selection's
     * persistent `aspectRatio` property itself is left untouched so the user still can't
     * drag-resize it out of the locked ratio afterward.
     */
    private _selectFullImage(): void {

        const imageBounds = this._getImageBoundsRelativeToCanvas();
        if (!this._cropperSelection || !imageBounds) {
            return;
        }
        const aspectRatio = this._cropperSelection.aspectRatio;
        if (!Number.isFinite(aspectRatio) || aspectRatio <= 0) {
            this._cropperSelection.$change(imageBounds.x, imageBounds.y, imageBounds.width, imageBounds.height);
            return;
        }
        let width = imageBounds.width;
        let height = width / aspectRatio;
        if (height > imageBounds.height) {
            height = imageBounds.height;
            width = height * aspectRatio;
        }
        const x = imageBounds.x + (imageBounds.width - width) / 2;
        const y = imageBounds.y + (imageBounds.height - height) / 2;
        this._cropperSelection.$change(x, y, width, height, NaN);
    }

    /**
     * Returns `<cropper-image>`'s current rendered bounds relative to `<cropper-canvas>`'s
     * origin — the same coordinate space `CropperSelection.x/y/width/height` use — so the
     * selection can be compared against (or set to match) the image's actual on-screen size
     * without a units mismatch. Shared by `_selectFullImage` (selecting the whole image) and
     * `_onCrop` (detecting whether the current selection still covers the whole image).
     */
    private _getImageBoundsRelativeToCanvas(): {
        x: number;
        y: number;
        width: number;
        height: number;
    } | null {

        const cropperCanvas = this._cropper?.getCropperCanvas();
        if (!this._cropperImage || !cropperCanvas) {
            return null;
        }
        const canvasRect = cropperCanvas.getBoundingClientRect();
        const imageRect = this._cropperImage.getBoundingClientRect();
        return {
            x: imageRect.left - canvasRect.left,
            y: imageRect.top - canvasRect.top,
            width: imageRect.width,
            height: imageRect.height
        };
    }
}
